import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function AdminTournamentSlots({ tournament: initialTournament, selectedDate, onClose }) {
    const [tournament, setTournament] = useState(initialTournament);
    const [isSaving, setIsSaving] = useState(false);
    
    // ID/PASS and Ready Check states
    const [idPassModalOpen, setIdPassModalOpen] = useState(false);
    const [idPassData, setIdPassData] = useState({ roomId: initialTournament.roomId || '', roomPassword: initialTournament.roomPassword || '' });

    useEffect(() => {
        if (!tournament.docId || !selectedDate) return;
        const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const latestMatches = docSnap.data().data || [];
                const updatedMatch = latestMatches.find(m => m.id === initialTournament.id && m.title === initialTournament.title);
                if (updatedMatch) {
                    setTournament(prev => ({ ...prev, ...updatedMatch }));
                    setIdPassData({ roomId: updatedMatch.roomId || '', roomPassword: updatedMatch.roomPassword || '' });
                }
            }
        });
        return () => unsubscribe();
    }, [initialTournament.id, initialTournament.title, tournament.docId, selectedDate]);

    const handlePublishIdPass = async () => {
        try {
            const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                let data = snap.data().data || [];
                const matchIndex = data.findIndex(m => m.id === tournament.id);
                if (matchIndex !== -1) {
                    data[matchIndex].roomId = idPassData.roomId;
                    data[matchIndex].roomPassword = idPassData.roomPassword;
                    await setDoc(docRef, { data }, { merge: true });
                    alert("Credentials Published Successfully!");
                    setIdPassModalOpen(false);
                }
            }
        } catch (error) {
            console.error(error);
            alert("Error publishing credentials!");
        }
    };

    const handleToggleReadyCheck = async () => {
        try {
            const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                let data = snap.data().data || [];
                const matchIndex = data.findIndex(m => m.id === tournament.id);
                if (matchIndex !== -1) {
                    data[matchIndex].isReadyCheckActive = !data[matchIndex].isReadyCheckActive;
                    await setDoc(docRef, { data }, { merge: true });
                }
            }
        } catch (error) {
            console.error("Error toggling ready check:", error);
            alert("Error toggling ready check!");
        }
    };

    const handleExportJSON = () => {
        if (!tournament.isCompleted) {
            alert("Match is not concluded yet!");
            return;
        }
        
        const bookedSlots = tournament.bookedSlots || [];
        const teamsMap = new Map();
        bookedSlots.forEach(slot => {
            if (!teamsMap.has(slot.teamIndex)) {
                teamsMap.set(slot.teamIndex, {
                    teamId: slot.teamIndex,
                    teamPosition: slot.teamIndex, // Default starting position
                    players: []
                });
            }
            teamsMap.get(slot.teamIndex).players.push({
                kills: 0,
                ign: slot.ign || "UNKNOWN",
                uid: slot.uid || "UNKNOWN"
            });
        });
        
        const jsonTemplate = Array.from(teamsMap.values());
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(jsonTemplate, null, 2));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        const uniqueMatchId = tournament.matchId ? `_ID_${tournament.matchId}` : "";
        downloadAnchorNode.setAttribute("download", `${tournament.title.replace(/[^a-z0-9]/gi, '_').toUpperCase()}${uniqueMatchId}_RESULTS.json`);
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const mode = tournament.mode?.toUpperCase() || 'SQUAD';
    const totalSlots = parseInt((tournament.slots || "0 / 100").split(' / ')[1]) || 100;

    let slotsPerTeam = 1;
    if (mode === 'DUO') slotsPerTeam = 2;
    if (mode === 'SQUAD') slotsPerTeam = 4;

    const numTeams = Math.ceil(totalSlots / slotsPerTeam);

    const [selectedSlot, setSelectedSlot] = useState(null); // { teamIndex, slotIndex }

    // Admin Popup states
    const [showAddPlayerPopup, setShowAddPlayerPopup] = useState(false);
    const [manualUid, setManualUid] = useState('');
    const [manualIgn, setManualIgn] = useState('');

    const [showManagePlayerPopup, setShowManagePlayerPopup] = useState(false);
    const [managePlayerSlot, setManagePlayerSlot] = useState(null);
    const [manageNewUid, setManageNewUid] = useState('');
    const [manageNewIgn, setManageNewIgn] = useState('');
    const [isFetchingManageUid, setIsFetchingManageUid] = useState(false);
    const [fetchManageSuccess, setFetchManageSuccess] = useState(false);
    const [fetchManageError, setFetchManageError] = useState('');
    const [fetchedUserProfile, setFetchedUserProfile] = useState(null);
    const [isFetchingUserProfile, setIsFetchingUserProfile] = useState(false);
    const [selectedAdminUid, setSelectedAdminUid] = useState('');

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!managePlayerSlot) return;
            setIsFetchingUserProfile(true);
            setFetchedUserProfile(null);
            try {
                const usersRef = collection(db, 'users');
                let q = query(usersRef, where('profileData.gameUid1', '==', managePlayerSlot.bookedSlot.uid));
                let querySnapshot = await getDocs(q);
                
                if (querySnapshot.empty) {
                    q = query(usersRef, where('profileData.gameUid2', '==', managePlayerSlot.bookedSlot.uid));
                    querySnapshot = await getDocs(q);
                }

                if (!querySnapshot.empty) {
                    setFetchedUserProfile({ id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() });
                }
            } catch (error) {
                console.error("Error fetching user profile:", error);
            } finally {
                setIsFetchingUserProfile(false);
            }
        };

        fetchUserProfile();
    }, [managePlayerSlot]);

    const handleRemovePlayer = async (teamIndex, slotIndex, bookedPlayer) => {
        setIsSaving(true);
        try {
            const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                let data = snap.data().data || [];
                const matchIndex = data.findIndex(m => m.id === tournament.id);
                if (matchIndex !== -1) {
                    let bookedSlots = data[matchIndex].bookedSlots || [];
                    
                    // Filter out this player
                    const newBookedSlots = bookedSlots.filter(s => !(s.teamIndex === teamIndex && s.slotIndex === slotIndex));
                    data[matchIndex].bookedSlots = newBookedSlots;
                    
                    // Update slots count
                    let [_, totalStr] = (data[matchIndex].slots || "0 / 100").split(' / ');
                    data[matchIndex].slots = `${newBookedSlots.length} / ${totalStr.trim()}`;
                    
                    await setDoc(docRef, { data }, { merge: true });
                }
            }
        } catch (e) {
            console.error("Error removing player:", e);
            alert("Error removing player.");
        } finally {
            setIsSaving(false);
            setSelectedSlot(null);
        }
    };

    const handleFetchManageUid = async () => {
        if (!manageNewUid) return;
        setIsFetchingManageUid(true);
        setFetchManageError('');
        setFetchManageSuccess(false);

        try {
            const response = await fetch(`https://bgmiesports.gridlabsproduction.workers.dev/?uid=${manageNewUid}`);
            const data = await response.json();

            if (data.success && data.username) {
                setManageNewIgn(data.username);
                setFetchManageSuccess(true);
                setFetchManageError('');
            } else {
                throw new Error("ACCOUNT NOT FOUND OR INVALID UID");
            }
        } catch (error) {
            setFetchManageError(error.message || 'FAILED TO FETCH UID');
            setFetchManageSuccess(false);
            setManageNewIgn('');
        } finally {
            setIsFetchingManageUid(false);
        }
    };

    const handleUpdateSecondUid = async () => {
        if (!managePlayerSlot || !manageNewUid || !manageNewIgn) return;
        setIsSaving(true);
        try {
            // Find user in users collection
            const usersRef = collection(db, 'users');
            
            // Try to find by gameUid1
            let q = query(usersRef, where('profileData.gameUid1', '==', managePlayerSlot.bookedSlot.uid));
            let querySnapshot = await getDocs(q);
            
            if (querySnapshot.empty) {
                // Try to find by gameUid2
                q = query(usersRef, where('profileData.gameUid2', '==', managePlayerSlot.bookedSlot.uid));
                querySnapshot = await getDocs(q);
            }

            if (!querySnapshot.empty) {
                const userDoc = querySnapshot.docs[0];
                const userId = userDoc.id;
                
                // Update the user's document
                await setDoc(doc(db, 'users', userId), { 
                    profileData: {
                        gameUid2: manageNewUid,
                        gameIgn2: manageNewIgn
                    }
                }, { merge: true });

                // Also update the slot booking in dailyMatches
                const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    let data = snap.data().data || [];
                    const matchIndex = data.findIndex(m => m.id === tournament.id);
                    if (matchIndex !== -1) {
                        let bookedSlots = data[matchIndex].bookedSlots || [];
                        const slotIndexToUpdate = bookedSlots.findIndex(s => s.teamIndex === managePlayerSlot.teamIndex && s.slotIndex === managePlayerSlot.slotIndex);
                        
                        if (slotIndexToUpdate !== -1) {
                            bookedSlots[slotIndexToUpdate].uid = manageNewUid;
                            bookedSlots[slotIndexToUpdate].ign = manageNewIgn;
                            
                            data[matchIndex].bookedSlots = bookedSlots;
                            await setDoc(docRef, { data }, { merge: true });
                        }
                    }
                }

                alert(`Successfully updated 2nd Game UID for user!`);
            } else {
                alert(`User not found in database with UID: ${managePlayerSlot.bookedSlot.uid}. Make sure they booked this slot through the platform.`);
            }
            
            setShowManagePlayerPopup(false);
        } catch (error) {
            console.error("Error updating 2nd UID:", error);
            alert("Error updating UID.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSwitchUid = async (newUid, newIgn) => {
        if (!managePlayerSlot) return;
        setIsSaving(true);
        try {
            const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                let data = snap.data().data || [];
                const matchIndex = data.findIndex(m => m.id === tournament.id);
                if (matchIndex !== -1) {
                    let bookedSlots = data[matchIndex].bookedSlots || [];
                    const slotIndexToUpdate = bookedSlots.findIndex(s => s.teamIndex === managePlayerSlot.teamIndex && s.slotIndex === managePlayerSlot.slotIndex);
                    
                    if (slotIndexToUpdate !== -1) {
                        bookedSlots[slotIndexToUpdate].uid = newUid;
                        bookedSlots[slotIndexToUpdate].ign = newIgn;
                        
                        data[matchIndex].bookedSlots = bookedSlots;
                        await setDoc(docRef, { data }, { merge: true });
                        alert("Slot switched to the other UID!");
                    }
                }
            }
            setShowManagePlayerPopup(false);
        } catch (error) {
            console.error("Error switching UID:", error);
            alert("Error switching UID.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddPlayerSubmit = async () => {
        if (!manualUid || !manualIgn || !selectedSlot) return;
        
        setIsSaving(true);
        try {
            const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', selectedDate);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                let data = snap.data().data || [];
                const matchIndex = data.findIndex(m => m.id === tournament.id);
                if (matchIndex !== -1) {
                    let bookedSlots = data[matchIndex].bookedSlots || [];
                    
                    // Check if slot already booked just in case
                    if (bookedSlots.some(s => s.teamIndex === selectedSlot.teamIndex && s.slotIndex === selectedSlot.slotIndex)) {
                        alert("Slot already booked!");
                        setShowAddPlayerPopup(false);
                        setIsSaving(false);
                        return;
                    }

                    const newBooking = {
                        teamIndex: selectedSlot.teamIndex,
                        slotIndex: selectedSlot.slotIndex,
                        uid: manualUid,
                        ign: manualIgn,
                        platform: 'ADMIN_MANUAL'
                    };
                    
                    bookedSlots.push(newBooking);
                    data[matchIndex].bookedSlots = bookedSlots;
                    
                    // Update slots count
                    let [_, totalStr] = (data[matchIndex].slots || "0 / 100").split(' / ');
                    data[matchIndex].slots = `${bookedSlots.length} / ${totalStr.trim()}`;
                    
                    await setDoc(docRef, { data }, { merge: true });
                }
            }
        } catch (e) {
            console.error("Error adding player:", e);
            alert("Error adding player.");
        } finally {
            setIsSaving(false);
            setShowAddPlayerPopup(false);
            setManualUid('');
            setManualIgn('');
            setSelectedSlot(null);
        }
    };

    let gridClass = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 min-[1680px]:grid-cols-4";
    if (mode === 'DUO') {
        gridClass = "grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 min-[1680px]:grid-cols-8";
    } else if (mode === 'SOLO') {
        gridClass = "grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 min-[1680px]:grid-cols-12";
    }

    return (
        <div className="w-full min-h-screen bg-transparent dark:bg-[#0a0a0f] font-['Monda'] uppercase text-gray-900 dark:text-white">
            <style>{`
                /* Hide scrollbar globally when slots page is active */
                html::-webkit-scrollbar,
                body::-webkit-scrollbar,
                *::-webkit-scrollbar {
                    display: none !important;
                }
                html, body, * {
                    -ms-overflow-style: none !important;  /* IE and Edge */
                    scrollbar-width: none !important;  /* Firefox */
                }
            `}</style>
            <div className="max-w-[120rem] mx-auto p-[5px] md:p-[30px]">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-[10px] md:pb-[15px] mb-[10px] md:mb-[15px] flex-wrap gap-4 md:gap-6">
                    <div className="w-full md:w-auto flex-1">
                        <div className="flex flex-col items-start gap-2 md:gap-4 mb-2 md:mb-4">
                            <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-500 flex items-center gap-1 md:gap-2 transition-colors text-sm md:text-base">
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                Back to Lobby
                            </button>
                            <h2 className="text-xl md:text-5xl font-black tracking-wider text-gray-900 dark:text-white flex items-center gap-1 md:gap-3 text-left">
                                {tournament.title} <span className="text-pink-600 dark:text-pink-500">ADMIN SLOTS</span>
                            </h2>
                        </div>
                        <div className="hidden md:flex flex-wrap gap-3 mt-3">
                            <span className={`px-3 py-1 text-sm font-bold rounded-none uppercase tracking-widest ${tournament.lobbyType === 'HACKER vs HACKER' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-green-500/10 text-green-500 border border-green-500/20'}`}>
                                {tournament.lobbyType || 'CLEAN LOBBY'}
                            </span>
                            <span className="bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 px-3 py-1 text-sm font-bold rounded-none border border-cyan-500/20">
                                {tournament.platform || 'MOBILE'}
                            </span>
                            <span className="bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 px-3 py-1 text-sm font-bold rounded-none border border-purple-500/20">{tournament.mode || 'SQUAD'}</span>
                            <span className="bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 px-3 py-1 text-sm font-bold rounded-none border border-blue-500/20">{tournament.map || 'ERANGEL'}</span>
                            <span className="bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-300 px-3 py-1 text-sm font-bold rounded-none border border-pink-500/20">{tournament.time}</span>
                            <span className="bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300 px-3 py-1 text-sm font-bold rounded-none border border-gray-500/20">{tournament.slots} SLOTS BOOKED</span>
                            <span className="bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 px-3 py-1 text-sm font-bold rounded-none border border-green-500/20">{(tournament.bookedSlots || []).filter(s => s.ready).length} / {(tournament.slots || "0 / 100").split(' / ')[1]} READY</span>
                        </div>
                    </div>
                    <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                        {tournament.isCompleted && (
                            <button 
                                onClick={handleExportJSON}
                                className="px-4 py-2 font-bold text-sm uppercase rounded shadow-sm whitespace-nowrap transition-colors border bg-purple-500 hover:bg-purple-600 text-white border-purple-500"
                            >
                                EXPORT JSON
                            </button>
                        )}
                        <button 
                            onClick={() => setIdPassModalOpen(true)}
                            disabled={false}
                            title={"Publish Credentials"}
                            className={`px-4 py-2 font-bold text-sm uppercase rounded shadow-sm whitespace-nowrap transition-colors border bg-blue-500 hover:bg-blue-600 text-white border-blue-500`}
                        >
                            ID/PASS
                        </button>
                        {tournament.roomId && tournament.roomPassword && (
                            <button 
                                onClick={handleToggleReadyCheck} 
                                className={`px-4 py-2 ${tournament.isReadyCheckActive ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'} text-white font-bold text-sm uppercase rounded shadow-sm whitespace-nowrap transition-colors`}
                            >
                                {tournament.isReadyCheckActive ? 'STOP READY CHECK' : 'READY CHECK'}
                            </button>
                        )}
                    </div>
                </div>
                {/* Slots Grid */}
                <div className={`flex-1 grid ${gridClass} gap-[5px] md:gap-[15px] pb-20`}>
                    {Array.from({ length: numTeams }).map((_, teamIndex) => (
                        <div
                            key={teamIndex}
                            className="uppercase font-['Monda'] relative overflow-hidden rounded-none p-[2px] group h-full"
                        >
                            <div className="absolute inset-[-150%] animate-[spin_4s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0%,#a855f7_25%,#ec4899_50%,#a855f7_75%,transparent_100%)] opacity-40 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="relative z-10 w-full h-full bg-white dark:bg-[#1a1a24]">
                                <div className="p-[15px] h-full flex flex-col justify-center">
                                    <div className={`flex items-center mb-4 border-b border-gray-200 dark:border-white/5 pb-2 ${mode === 'SOLO' ? 'justify-center' : 'justify-between'}`}>
                                        <h3 className="text-xl font-black text-gray-900 dark:text-white whitespace-nowrap">
                                            {mode === 'SOLO' ? `SLOT ${teamIndex + 1}` : `TEAM ${teamIndex + 1}`}
                                        </h3>
                                        {mode !== 'SOLO' && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 font-bold ml-2">{slotsPerTeam} SLOTS</span>
                                        )}
                                    </div>

                                    <div
                                        className="w-full grid gap-[15px]"
                                        style={{ gridTemplateColumns: `repeat(${slotsPerTeam}, minmax(0, 1fr))` }}
                                    >
                                        {Array.from({ length: slotsPerTeam }).map((_, slotIndex) => {
                                            const bookedSlot = (tournament.bookedSlots || []).find(s => s.teamIndex === teamIndex && s.slotIndex === slotIndex);
                                            
                                            return (
                                                <button
                                                    key={slotIndex}
                                                    disabled={isSaving}
                                                    onClick={() => {
                                                        if (bookedSlot) {
                                                            setManagePlayerSlot({ teamIndex, slotIndex, bookedSlot });
                                                            setSelectedAdminUid(bookedSlot.uid);
                                                            setManageNewUid('');
                                                            setManageNewIgn('');
                                                            setFetchManageSuccess(false);
                                                            setFetchManageError('');
                                                            setShowManagePlayerPopup(true);
                                                        } else {
                                                            setSelectedSlot({ teamIndex, slotIndex });
                                                            setShowAddPlayerPopup(true);
                                                        }
                                                    }}
                                                    className={`relative w-full aspect-square rounded-none border-2 overflow-hidden transition-all flex flex-col items-center justify-center group/slot ${
                                                        bookedSlot
                                                        ? `bg-gray-200 dark:bg-[#0a0a0f] ${bookedSlot.ready ? 'border-green-500/50' : 'border-red-500/50 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.3)]'}`
                                                        : 'bg-gray-50 dark:bg-[#111] border-gray-300 dark:border-gray-600 hover:border-pink-500 hover:text-pink-500'
                                                    }`}
                                                >
                                                    {bookedSlot ? (
                                                        <>
                                                            <img src="https://images.unsplash.com/photo-1566411520896-01e7ca4726af?auto=format&fit=crop&w=150&q=80" alt="Booked" className="w-full h-full object-cover opacity-80" />
                                                            {bookedSlot.ready && (
                                                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-10">
                                                                    <svg className="w-8 h-8 text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.8)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-0 bg-red-500/20 opacity-0 group-hover/slot:opacity-100 flex flex-col items-center justify-start pt-2 transition-opacity z-20">
                                                                <span className="text-red-500 font-bold text-[10px]">MODIFY</span>
                                                            </div>
                                                            <div className="absolute bottom-0 w-full bg-black/60 backdrop-blur-sm text-white text-[8px] font-black tracking-widest text-center py-0.5 uppercase truncate px-1 border-t border-white/10 group-hover/slot:opacity-0 transition-opacity z-20">{bookedSlot.ign}</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-2xl font-light transition-transform group-hover/slot:scale-110 text-gray-400 dark:text-gray-500">+</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Player Popup */}
            {showAddPlayerPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#1a1a24] p-6 max-w-md w-full border border-gray-200 dark:border-white/10 shadow-2xl relative">
                        <button onClick={() => setShowAddPlayerPopup(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <h2 className="text-xl font-black uppercase tracking-wider mb-2">ADD PLAYER TO SLOT</h2>
                        <p className="text-xs text-gray-400 uppercase tracking-widest mb-6 font-bold">
                            {mode === 'SOLO' ? `SLOT ${selectedSlot.teamIndex + 1}` : `TEAM ${selectedSlot.teamIndex + 1} - SLOT ${selectedSlot.slotIndex + 1}`}
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Game UID</label>
                                <input 
                                    type="text" 
                                    value={manualUid} 
                                    onChange={(e) => setManualUid(e.target.value)} 
                                    className="w-full bg-gray-50 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-none px-4 py-3 outline-none font-mono focus:border-pink-500 transition-colors"
                                    placeholder="Enter Player UID"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">In-Game Name (IGN)</label>
                                <input 
                                    type="text" 
                                    value={manualIgn} 
                                    onChange={(e) => setManualIgn(e.target.value)} 
                                    className="w-full bg-gray-50 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-none px-4 py-3 outline-none focus:border-pink-500 transition-colors font-bold uppercase"
                                    placeholder="Enter Player IGN"
                                />
                            </div>
                        </div>

                        <div className="mt-8 flex gap-3">
                            <button 
                                onClick={() => setShowAddPlayerPopup(false)} 
                                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-white/5 hover:bg-gray-300 dark:hover:bg-white/10 transition-colors uppercase font-bold text-xs tracking-widest"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddPlayerSubmit}
                                disabled={!manualUid || !manualIgn || isSaving}
                                className="flex-1 px-4 py-3 bg-pink-500 hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors uppercase font-bold text-xs tracking-widest"
                            >
                                {isSaving ? 'SAVING...' : 'ADD PLAYER'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Player Popup */}
            {showManagePlayerPopup && managePlayerSlot && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm cursor-default" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white dark:bg-[#1a1a24] p-[20px] rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl w-full max-w-lg relative">
                        <button onClick={() => setShowManagePlayerPopup(false)} className="absolute top-[15px] right-[15px] text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors z-10">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>

                        <div className="border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px] text-center">
                            <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-wider font-['Monda']">MANAGE PLAYER</h3>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1 truncate">
                                {mode === 'SOLO' ? `SLOT ${managePlayerSlot.teamIndex + 1}` : `TEAM ${managePlayerSlot.teamIndex + 1} - SLOT ${managePlayerSlot.slotIndex + 1}`}
                                {' '} | {managePlayerSlot.bookedSlot.ign} ({managePlayerSlot.bookedSlot.uid})
                            </p>
                        </div>
                        
                        <div className="mb-[15px]">
                            <button 
                                onClick={() => {
                                    handleRemovePlayer(managePlayerSlot.teamIndex, managePlayerSlot.slotIndex, managePlayerSlot.bookedSlot);
                                    setShowManagePlayerPopup(false);
                                }}
                                disabled={isSaving}
                                className="w-full px-4 py-3 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 border border-red-200 dark:border-red-500/20 transition-colors uppercase font-bold text-xs tracking-widest flex items-center justify-center gap-2 rounded-xl"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Remove Player From Slot
                            </button>
                        </div>

                        <div className="border-t border-gray-200 dark:border-white/10 pt-[15px]">
                            {isFetchingUserProfile ? (
                                <p className="text-xs text-gray-500 font-bold uppercase animate-pulse text-center my-4">Loading player profile...</p>
                            ) : fetchedUserProfile ? (
                                (() => {
                                    const prof = fetchedUserProfile.profileData || {};
                                    const hasTwoUids = (prof.gameUid1 && prof.gameIgn1) && (prof.gameUid2 && prof.gameIgn2);
                                    const currentUid = managePlayerSlot.bookedSlot.uid;
                                    
                                    if (hasTwoUids) {
                                        return (
                                            <div className="flex flex-col gap-5">
                                                <div>
                                                    <h4 className="text-base font-black text-gray-900 dark:text-white mb-3 uppercase tracking-widest font-['Monda']">SELECT GAME UID</h4>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {[1, 2].map(num => {
                                                            const uid = prof[`gameUid${num}`];
                                                            const ign = prof[`gameIgn${num}`];
                                                            const isSelected = selectedAdminUid === uid;
                                                            return (
                                                                <button
                                                                    key={uid}
                                                                    onClick={() => setSelectedAdminUid(uid)}
                                                                    className={`p-3 rounded-xl border-2 text-left transition-all uppercase flex flex-col justify-center h-[90px] ${isSelected ? 'border-[#ff4b8b] bg-pink-50 dark:bg-[#ff4b8b]/10' : 'border-gray-200 dark:border-white/10 hover:border-[#ff4b8b]/50 dark:hover:border-white/20'}`}
                                                                >
                                                                    <div className="text-[10px] font-bold text-gray-500 mb-1 uppercase tracking-widest">ACCOUNT</div>
                                                                    <div className="text-lg font-black text-gray-900 dark:text-white font-mono tracking-wider truncate w-full">{uid}</div>
                                                                    <div className="text-[10px] font-bold text-[#ff4b8b] mt-1 uppercase tracking-widest truncate w-full">{ign}</div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                
                                                <div className="flex gap-[15px] mt-[10px]">
                                                    <button 
                                                        onClick={() => setShowManagePlayerPopup(false)}
                                                        className="flex-1 py-3 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-black uppercase tracking-widest text-sm rounded-[50px] hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                                    >
                                                        CANCEL
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const newIgn = prof.gameUid1 === selectedAdminUid ? prof.gameIgn1 : prof.gameIgn2;
                                                            handleSwitchUid(selectedAdminUid, newIgn);
                                                        }}
                                                        disabled={selectedAdminUid === currentUid || isSaving}
                                                        className="flex-1 py-3 bg-[#ff4b8b] hover:bg-[#ff3377] text-white font-black uppercase tracking-widest text-sm rounded-[50px] transition-all disabled:opacity-50 shadow-lg shadow-pink-500/30"
                                                    >
                                                        {isSaving ? 'UPDATING...' : 'UPDATE SLOT'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div className="space-y-4">
                                                <div>
                                                    <h4 className="text-base font-black text-gray-900 dark:text-white mb-3 uppercase tracking-widest font-['Monda']">SET & SWITCH 2ND UID</h4>
                                                    <label className="text-xs text-gray-500 dark:text-gray-400 font-bold mb-2 block uppercase tracking-wider">Fetch & Set 2nd UID</label>
                                                    <div className="flex gap-2">
                                                        <input 
                                                            type="text" 
                                                            value={manageNewUid} 
                                                            onChange={(e) => {
                                                                setManageNewUid(e.target.value);
                                                                setFetchManageSuccess(false);
                                                                setFetchManageError('');
                                                                setManageNewIgn('');
                                                            }} 
                                                            className="flex-1 bg-gray-50 dark:bg-black/50 border border-gray-300 dark:border-white/10 rounded-xl px-4 py-3 outline-none font-black text-sm focus:border-pink-500 transition-colors min-w-0 dark:text-white uppercase"
                                                            placeholder="Enter UID"
                                                        />
                                                        <button 
                                                            onClick={handleFetchManageUid}
                                                            disabled={!manageNewUid || isFetchingManageUid}
                                                            className="px-4 py-3 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-900 dark:text-white transition-colors uppercase font-bold text-xs tracking-widest disabled:opacity-50 whitespace-nowrap rounded-xl"
                                                        >
                                                            {isFetchingManageUid ? 'Fetching...' : 'Fetch'}
                                                        </button>
                                                    </div>
                                                    {fetchManageError && <p className="text-red-500 text-xs mt-2 font-bold">{fetchManageError}</p>}
                                                </div>
                                                {fetchManageSuccess && (
                                                    <div className="p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-xl flex items-center justify-between">
                                                        <div className="truncate pr-2">
                                                            <p className="text-[10px] text-green-600 dark:text-green-500 uppercase font-bold tracking-wider mb-1">Account Found</p>
                                                            <p className="font-black text-gray-900 dark:text-white truncate">{manageNewIgn}</p>
                                                        </div>
                                                        <button 
                                                            onClick={handleUpdateSecondUid}
                                                            disabled={isSaving}
                                                            className="px-4 py-2 bg-[#ff4b8b] hover:bg-[#ff3377] text-white font-bold text-xs uppercase tracking-widest disabled:opacity-50 whitespace-nowrap rounded-[50px] shadow-lg shadow-pink-500/30 transition-all"
                                                        >
                                                            {isSaving ? 'UPDATING...' : 'UPDATE'}
                                                        </button>
                                                    </div>
                                                )}

                                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                                                    <button 
                                                        onClick={() => setShowManagePlayerPopup(false)} 
                                                        className="w-full py-3 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-black uppercase tracking-widest text-sm rounded-[50px] hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                                    >
                                                        CANCEL / CLOSE
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }
                                })()
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-xs text-red-500 font-bold uppercase text-center mt-2">Player profile not found in database.</p>
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                                        <button 
                                            onClick={() => setShowManagePlayerPopup(false)} 
                                            className="w-full py-3 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-black uppercase tracking-widest text-sm rounded-[50px] hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                        >
                                            CANCEL / CLOSE
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* ID/PASS Modal */}
            {idPassModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIdPassModalOpen(false)}></div>
                    <div className="relative w-full max-w-sm bg-[#1a1a24] border border-white/10 rounded-2xl shadow-2xl overflow-hidden p-6">
                        <button onClick={() => setIdPassModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        
                        <h3 className="text-xl font-black text-white uppercase tracking-wider mb-2 border-b border-white/10 pb-4">PUBLISH ID/PASS</h3>
                        <p className="text-sm text-gray-400 mb-6 uppercase">
                            Enter Room ID and Password for <span className="text-pink-500 font-bold">{tournament.title}</span>
                        </p>
                        
                        <div className="mb-4">
                            <label className="text-xs text-gray-500 font-bold mb-2 block uppercase tracking-wider">ROOM ID</label>
                            <input 
                                type="text" 
                                value={idPassData.roomId}
                                onChange={(e) => setIdPassData({...idPassData, roomId: e.target.value})}
                                placeholder="Enter Room ID"
                                className="w-full font-black text-sm bg-black/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-white"
                                autoFocus
                            />
                        </div>

                        <div className="mb-6">
                            <label className="text-xs text-gray-500 font-bold mb-2 block uppercase tracking-wider">PASSWORD</label>
                            <input 
                                type="text" 
                                value={idPassData.roomPassword}
                                onChange={(e) => setIdPassData({...idPassData, roomPassword: e.target.value})}
                                placeholder="Enter Password"
                                className="w-full font-black text-sm bg-black/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-white"
                            />
                        </div>
                        
                        <div className="flex gap-4">
                            <button onClick={() => setIdPassModalOpen(false)} className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-black text-sm uppercase transition-colors">
                                CANCEL
                            </button>
                            <button onClick={handlePublishIdPass} className="flex-[2] px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-black text-sm uppercase transition-colors shadow-lg shadow-blue-500/30">
                                PUBLISH
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
